import { createSelector } from "@reduxjs/toolkit";
import {
  MILESTONE_LOYALTY_MECHANISMS,
  TIERED_LOYALTY_MECHANISMS,
} from "../../../utils/loyaltyTypes";
import type { LoyaltyMechanism } from "../../../utils/loyaltyTypes";
import type { RootState } from "../app/store";

// The legacy cart path is gone, so the restaurant's flag no longer chooses one.
export const selectUseLoyaltyService = (_state: RootState) => true;

const selectTierSystemMatchesActiveRestaurant = (state: RootState): boolean => {
  const activeRestaurantId =
    state.config.selectedFranchise?.restaurantId ??
    state.config.restaurantInfo?.id;

  return (
    activeRestaurantId !== undefined &&
    state.loyaltyTierSystem?.restaurantId === activeRestaurantId
  );
};

/**
 * The rollout gate for the new guest storefront (loyalty-service
 * `GUEST_TIERS_UI`). It says a brand may have the new UI at all; it never says
 * which parts of it - that is the mechanism's call below.
 */
const selectNewLoyaltyUiGate = (state: RootState): boolean =>
  selectUseLoyaltyService(state) &&
  selectTierSystemMatchesActiveRestaurant(state) &&
  state.loyaltyTierSystem?.data?.newLoyaltyUiEnabled === true;

const selectMechanism = (state: RootState): LoyaltyMechanism | undefined =>
  state.loyaltyTierSystem?.data?.mechanism;

const runsMechanism = (
  mechanisms: readonly LoyaltyMechanism[],
  state: RootState
): boolean => {
  const mechanism = selectMechanism(state);

  return mechanism !== undefined && mechanisms.includes(mechanism);
};

/**
 * Three gates, in order: the restaurant runs loyalty-service at all, its
 * configured mechanism is one that moves members between tiers, and the brand is
 * on the new-UI rollout. Deriving the middle step from the mechanism is what
 * keeps this off a per-restaurant flag - a chain set up on
 * `*_AND_TIERS` is a tiered chain, and nobody has to say so twice.
 *
 * A missing mechanism fails safe to the legacy layout, matching the app
 * (`LoyaltySlice.hasTieredMechanism`).
 */
export const selectGuestTiersEnabled = (state: RootState): boolean =>
  selectNewLoyaltyUiGate(state) &&
  runsMechanism(TIERED_LOYALTY_MECHANISMS, state);

/**
 * Same three gates, but for the milestone surfaces - the rewards meter and the
 * reward cards. The PAY_WITH_POINTS mechanisms spend points against the bill
 * rather than on milestones, so they get the tier UI without a meter.
 */
export const selectGuestMilestonesEnabled = (state: RootState): boolean =>
  selectNewLoyaltyUiGate(state) &&
  runsMechanism(MILESTONE_LOYALTY_MECHANISMS, state);

/**
 * Whether any tier has milestones configured at all. With none, the meter has
 * nothing to plot - it would draw a bar with no markers - so the screens leave it
 * out entirely.
 */
export const selectHasAnyTierMilestones = (state: RootState): boolean =>
  selectTierSystemMatchesActiveRestaurant(state) &&
  (state.loyaltyTierSystem.data?.tiers ?? []).some(
    (tier) => (tier.milestones?.length ?? 0) > 0
  );

export const selectIsVerifiedOrganizationCustomer = (
  state: RootState
): boolean => {
  if (selectUseLoyaltyService(state)) {
    return !!state.loyaltyCustomerInfo?.data?.partnership?.isVerified;
  }

  return !!state.partnerAccount.organizationCustomer?.verified;
};

/**
 * The tier ids this build has loaded. Memoized because consumers compare by
 * reference: an inline `.map()` in a component hands back a new array on every
 * store notification and re-renders it on every dispatch.
 */
export const selectLoadedTierIds = createSelector(
  [(state: RootState) => state.loyaltyTierSystem.data?.tiers],
  (tiers) => (tiers ?? []).map((tier) => tier.tierId)
);
